/**
 * InsuranceManager Component
 *
 * Manages contractor insurance policies with:
 * - Add/Edit/Delete insurance policies
 * - Certificate of Insurance (COI) upload
 * - Expiration tracking and alerts
 * - Coverage amount validation
 */

import { useState, useEffect } from 'react';
import { Timestamp, collection, doc, setDoc, deleteDoc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Upload,
  Shield,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ExternalLink,
  Loader2,
  FileText,
} from 'lucide-react';
import {
  InsurancePolicy,
  InsuranceType,
  InsuranceStatus,
  INSURANCE_TYPE_LABELS,
  MINIMUM_COVERAGE_AMOUNTS,
  isExpired,
  isExpiringSoon,
  daysUntilExpiration,
  formatCurrency,
} from '@/types/contractor';

interface InsuranceManagerProps {
  contractorId: string;
}

export function InsuranceManager({ contractorId }: InsuranceManagerProps) {
  const { user } = useAuth();
  const { toast } = useToast();

  const [policies, setPolicies] = useState<InsurancePolicy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<InsurancePolicy | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    insuranceType: '' as InsuranceType,
    policyNumber: '',
    carrier: '',
    coverageAmount: '',
    perOccurrenceLimit: '',
    aggregateLimit: '',
    effectiveDate: '',
    expirationDate: '',
    namedInsured: '',
    agentName: '',
    agentPhone: '',
    agentEmail: '',
    wcExempt: false,
  });
  const [coiFile, setCoiFile] = useState<File | null>(null);

  // Fetch insurance policies from Firestore
  useEffect(() => {
    if (!contractorId) return;

    const policiesRef = collection(db, 'contractors', contractorId, 'insurance');
    const q = query(policiesRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const policyData: InsurancePolicy[] = [];
      snapshot.forEach((doc) => {
        policyData.push({ id: doc.id, ...doc.data() } as InsurancePolicy);
      });
      setPolicies(policyData);
      setIsLoading(false);
    }, (error) => {
      console.error('Error fetching insurance policies:', error);
      toast({
        title: 'Error',
        description: 'Failed to load insurance policies',
        variant: 'destructive',
      });
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [contractorId, toast]);

  // Reset form
  const resetForm = () => {
    setFormData({
      insuranceType: '' as InsuranceType,
      policyNumber: '',
      carrier: '',
      coverageAmount: '',
      perOccurrenceLimit: '',
      aggregateLimit: '',
      effectiveDate: '',
      expirationDate: '',
      namedInsured: '',
      agentName: '',
      agentPhone: '',
      agentEmail: '',
      wcExempt: false,
    });
    setCoiFile(null);
    setSelectedPolicy(null);
  };

  // Open dialog for adding new policy
  const handleAddPolicy = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  // Open dialog for editing policy
  const handleEditPolicy = (policy: InsurancePolicy) => {
    setSelectedPolicy(policy);
    setFormData({
      insuranceType: policy.insuranceType,
      policyNumber: policy.policyNumber,
      carrier: policy.carrier,
      coverageAmount: policy.coverageAmount?.toString() || '',
      perOccurrenceLimit: policy.perOccurrenceLimit?.toString() || '',
      aggregateLimit: policy.aggregateLimit?.toString() || '',
      effectiveDate: policy.effectiveDate,
      expirationDate: policy.expirationDate,
      namedInsured: policy.namedInsured,
      agentName: policy.agentName || '',
      agentPhone: policy.agentPhone || '',
      agentEmail: policy.agentEmail || '',
      wcExempt: policy.wcExempt || false,
    });
    setIsDialogOpen(true);
  };

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Invalid file type',
          description: 'Please upload a PDF, JPEG, or PNG file',
          variant: 'destructive',
        });
        return;
      }
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: 'Maximum file size is 10MB',
          variant: 'destructive',
        });
        return;
      }
      setCoiFile(file);
    }
  };

  // Save insurance policy
  const handleSavePolicy = async () => {
    // Validation
    if (!formData.insuranceType || !formData.carrier || !formData.expirationDate) {
      toast({
        title: 'Missing required fields',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    // Check minimum coverage for certain types
    const minCoverage = MINIMUM_COVERAGE_AMOUNTS[formData.insuranceType];
    const coverage = parseFloat(formData.coverageAmount) || 0;
    if (minCoverage && coverage < minCoverage) {
      toast({
        title: 'Coverage too low',
        description: `Minimum coverage for ${INSURANCE_TYPE_LABELS[formData.insuranceType]} is ${formatCurrency(minCoverage)}`,
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);

    try {
      let certificateUrl = selectedPolicy?.certificateUrl;

      // Upload file if selected
      if (coiFile) {
        const timestamp = Date.now();
        const storagePath = `insurance/${contractorId}/${timestamp}-${coiFile.name}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, coiFile);
        certificateUrl = await getDownloadURL(storageRef);
      }

      // Determine status based on expiration date
      let status: InsuranceStatus = 'active';
      if (isExpired(formData.expirationDate)) {
        status = 'expired';
      } else if (isExpiringSoon(formData.expirationDate, 60)) {
        status = 'pending_renewal';
      }

      const policyData = {
        insuranceType: formData.insuranceType,
        policyNumber: formData.policyNumber || null,
        carrier: formData.carrier,
        coverageAmount: parseFloat(formData.coverageAmount) || null,
        perOccurrenceLimit: parseFloat(formData.perOccurrenceLimit) || null,
        aggregateLimit: parseFloat(formData.aggregateLimit) || null,
        effectiveDate: formData.effectiveDate || null,
        expirationDate: formData.expirationDate,
        status,
        namedInsured: formData.namedInsured || null,
        agentName: formData.agentName || null,
        agentPhone: formData.agentPhone || null,
        agentEmail: formData.agentEmail || null,
        wcExempt: formData.wcExempt,
        certificateUrl: certificateUrl || null,
        updatedAt: Timestamp.now(),
      };

      if (selectedPolicy) {
        // Update existing policy
        const policyRef = doc(db, 'contractors', contractorId, 'insurance', selectedPolicy.id);
        await setDoc(policyRef, policyData, { merge: true });
        toast({
          title: 'Policy updated',
          description: 'Your insurance policy has been updated successfully',
        });
      } else {
        // Create new policy
        const newPolicyRef = doc(collection(db, 'contractors', contractorId, 'insurance'));
        await setDoc(newPolicyRef, {
          ...policyData,
          id: newPolicyRef.id,
          createdAt: Timestamp.now(),
        });
        toast({
          title: 'Policy added',
          description: 'Your insurance policy has been added successfully',
        });
      }

      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving policy:', error);
      toast({
        title: 'Error',
        description: 'Failed to save policy. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Delete policy
  const handleDeletePolicy = async () => {
    if (!selectedPolicy) return;

    try {
      const policyRef = doc(db, 'contractors', contractorId, 'insurance', selectedPolicy.id);
      await deleteDoc(policyRef);
      toast({
        title: 'Policy deleted',
        description: 'The insurance policy has been removed',
      });
      setIsDeleteDialogOpen(false);
      setSelectedPolicy(null);
    } catch (error) {
      console.error('Error deleting policy:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete policy',
        variant: 'destructive',
      });
    }
  };

  // Get status badge
  const getStatusBadge = (policy: InsurancePolicy) => {
    if (isExpired(policy.expirationDate)) {
      return (
        <Badge variant="destructive" className="flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          Expired
        </Badge>
      );
    }
    if (isExpiringSoon(policy.expirationDate, 60)) {
      const days = daysUntilExpiration(policy.expirationDate);
      return (
        <Badge variant="outline" className="text-amber-600 border-amber-600 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Expires in {days} days
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-green-600 border-green-600 flex items-center gap-1">
        <CheckCircle2 className="h-3 w-3" />
        Active
      </Badge>
    );
  };

  // Get coverage badge
  const getCoverageBadge = (policy: InsurancePolicy) => {
    const minCoverage = MINIMUM_COVERAGE_AMOUNTS[policy.insuranceType];
    if (!minCoverage || !policy.coverageAmount) return null;

    if (policy.coverageAmount < minCoverage) {
      return (
        <Badge variant="outline" className="text-amber-600 border-amber-600 text-xs">
          Below minimum
        </Badge>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Insurance</CardTitle>
              <CardDescription>
                Manage your insurance policies and certificates
              </CardDescription>
            </div>
            <Button onClick={handleAddPolicy} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Policy
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {policies.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No insurance policies added yet</p>
              <p className="text-sm mt-1">Add your insurance to keep track of renewals</p>
            </div>
          ) : (
            <div className="space-y-4">
              {policies.map((policy) => (
                <div
                  key={policy.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-medium">
                        {INSURANCE_TYPE_LABELS[policy.insuranceType] || policy.insuranceType}
                      </span>
                      {getStatusBadge(policy)}
                      {getCoverageBadge(policy)}
                      {policy.wcExempt && (
                        <Badge variant="secondary" className="text-xs">
                          WC Exempt
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground space-y-0.5">
                      <p>
                        <span className="font-medium">Carrier:</span> {policy.carrier}
                      </p>
                      {policy.policyNumber && (
                        <p>
                          <span className="font-medium">Policy #:</span> {policy.policyNumber}
                        </p>
                      )}
                      {policy.coverageAmount && (
                        <p>
                          <span className="font-medium">Coverage:</span>{' '}
                          {formatCurrency(policy.coverageAmount)}
                          {policy.perOccurrenceLimit && (
                            <> / {formatCurrency(policy.perOccurrenceLimit)} per occurrence</>
                          )}
                        </p>
                      )}
                      <p>
                        <span className="font-medium">Expires:</span>{' '}
                        {new Date(policy.expirationDate).toLocaleDateString()}
                      </p>
                    </div>
                    {policy.certificateUrl && (
                      <a
                        href={policy.certificateUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-primary mt-2 hover:underline"
                      >
                        <FileText className="h-3 w-3" />
                        View COI
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEditPolicy(policy)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => {
                          setSelectedPolicy(policy);
                          setIsDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Insurance Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        setIsDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedPolicy ? 'Edit Insurance Policy' : 'Add Insurance Policy'}
            </DialogTitle>
            <DialogDescription>
              {selectedPolicy
                ? 'Update your insurance policy information'
                : 'Add a new insurance policy to your profile'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="insuranceType">Insurance Type *</Label>
              <Select
                value={formData.insuranceType}
                onValueChange={(value) =>
                  setFormData({ ...formData, insuranceType: value as InsuranceType })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select insurance type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(INSURANCE_TYPE_LABELS).map(([code, label]) => (
                    <SelectItem key={code} value={code}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="carrier">Insurance Carrier *</Label>
              <Input
                id="carrier"
                value={formData.carrier}
                onChange={(e) =>
                  setFormData({ ...formData, carrier: e.target.value })
                }
                placeholder="e.g., State Farm, Progressive"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="policyNumber">Policy Number</Label>
              <Input
                id="policyNumber"
                value={formData.policyNumber}
                onChange={(e) =>
                  setFormData({ ...formData, policyNumber: e.target.value })
                }
                placeholder="Optional"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="coverageAmount">Coverage Limit ($)</Label>
                <Input
                  id="coverageAmount"
                  type="number"
                  value={formData.coverageAmount}
                  onChange={(e) =>
                    setFormData({ ...formData, coverageAmount: e.target.value })
                  }
                  placeholder="e.g., 1000000"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="perOccurrenceLimit">Per Occurrence ($)</Label>
                <Input
                  id="perOccurrenceLimit"
                  type="number"
                  value={formData.perOccurrenceLimit}
                  onChange={(e) =>
                    setFormData({ ...formData, perOccurrenceLimit: e.target.value })
                  }
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="namedInsured">Named Insured</Label>
              <Input
                id="namedInsured"
                value={formData.namedInsured}
                onChange={(e) =>
                  setFormData({ ...formData, namedInsured: e.target.value })
                }
                placeholder="Company or person name on policy"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="effectiveDate">Effective Date</Label>
                <Input
                  id="effectiveDate"
                  type="date"
                  value={formData.effectiveDate}
                  onChange={(e) =>
                    setFormData({ ...formData, effectiveDate: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="expirationDate">Expiration Date *</Label>
                <Input
                  id="expirationDate"
                  type="date"
                  value={formData.expirationDate}
                  onChange={(e) =>
                    setFormData({ ...formData, expirationDate: e.target.value })
                  }
                />
              </div>
            </div>

            {/* Agent Info */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-sm font-medium">Agent Information (Optional)</Label>
              <div className="grid grid-cols-1 gap-4 mt-2">
                <Input
                  placeholder="Agent Name"
                  value={formData.agentName}
                  onChange={(e) =>
                    setFormData({ ...formData, agentName: e.target.value })
                  }
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    placeholder="Agent Phone"
                    type="tel"
                    value={formData.agentPhone}
                    onChange={(e) =>
                      setFormData({ ...formData, agentPhone: e.target.value })
                    }
                  />
                  <Input
                    placeholder="Agent Email"
                    type="email"
                    value={formData.agentEmail}
                    onChange={(e) =>
                      setFormData({ ...formData, agentEmail: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Workers Comp Exempt Toggle */}
            {formData.insuranceType === 'workers_compensation' && (
              <div className="flex items-center justify-between border-t pt-4">
                <div>
                  <Label htmlFor="wcExempt">Workers' Comp Exempt</Label>
                  <p className="text-sm text-muted-foreground">
                    Check if you have a WC exemption on file
                  </p>
                </div>
                <Switch
                  id="wcExempt"
                  checked={formData.wcExempt}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, wcExempt: checked })
                  }
                />
              </div>
            )}

            <div className="space-y-2 border-t pt-4">
              <Label htmlFor="coiFile">Certificate of Insurance (COI)</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="coiFile"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="flex-1"
                />
              </div>
              {coiFile && (
                <p className="text-sm text-muted-foreground">
                  Selected: {coiFile.name}
                </p>
              )}
              {selectedPolicy?.certificateUrl && !coiFile && (
                <p className="text-sm text-muted-foreground">
                  Current COI on file
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePolicy} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  {coiFile && <Upload className="h-4 w-4 mr-2" />}
                  {selectedPolicy ? 'Update' : 'Add'} Policy
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Insurance Policy?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this insurance policy from your profile.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePolicy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
